import jsPDF from 'jspdf'
import { Lesson } from '../src/types/lesson'
import { Task } from './taskService'
import {
  getLessonDisplayStatus,
  LessonDisplayStatus,
} from '../utils/lessonStatusHelpers'

// Canonical display labels for the PDF, mirroring the badge labels used
// across the live dashboard / student schedule. Keep in sync with
// utils/lessonStatusHelpers.ts -> getLessonStatusBadge.
const PDF_STATUS_LABEL: Record<LessonDisplayStatus, string> = {
  scheduled: 'Scheduled',
  today: 'Today',
  'in-progress': 'In Progress',
  'needs-confirmation': 'Needs Confirmation',
  completed: 'Completed',
  'no-show': 'No-Show',
  cancelled: 'Cancelled',
}

export interface ProgressReportOptions {
  studentName: string
  instrument: string
  teacherName: string
  dateRange: { from: Date; to: Date }
  sections: {
    lessonHistory: boolean
    lessonNotes: boolean
    completedTasks: boolean
    progressSummary: boolean
  }
  lessons: Lesson[]
  tasks: Task[]
  metrics?: {
    attendanceRate: number
    taskCompletionRate: string
    progressTrend: string
  }
  branding?: {
    headerColor?: [number, number, number]
    logoBase64?: string
  }
  messageToParent?: string
}

// --- Manual table drawing helpers (no jspdf-autotable dependency) ---

interface TableColumn {
  header: string
  width: number
}

function drawTableHeader(
  doc: jsPDF,
  columns: TableColumn[],
  x: number,
  y: number,
  rowHeight: number,
  fillColor: [number, number, number],
  textColor: [number, number, number],
): number {
  doc.setFillColor(fillColor[0], fillColor[1], fillColor[2])
  const totalWidth = columns.reduce((sum, col) => sum + col.width, 0)
  doc.rect(x, y, totalWidth, rowHeight, 'F')

  doc.setFontSize(9)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(textColor[0], textColor[1], textColor[2])

  let cellX = x
  columns.forEach((col) => {
    doc.text(col.header, cellX + 6, y + rowHeight / 2 + 3)
    cellX += col.width
  })

  return y + rowHeight
}

function drawTableRow(
  doc: jsPDF,
  columns: TableColumn[],
  values: string[],
  x: number,
  y: number,
  rowHeight: number,
  isStriped: boolean,
): number {
  if (isStriped) {
    doc.setFillColor(248, 248, 248)
    const totalWidth = columns.reduce((sum, col) => sum + col.width, 0)
    doc.rect(x, y, totalWidth, rowHeight, 'F')
  }

  doc.setFontSize(9)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(50, 50, 50)

  let cellX = x
  columns.forEach((col, i) => {
    const text = values[i] || '—'
    // Truncate text to fit column width
    const maxChars = Math.floor((col.width - 12) / 4.5)
    const displayText =
      text.length > maxChars ? text.substring(0, maxChars - 2) + '...' : text
    doc.text(displayText, cellX + 6, y + rowHeight / 2 + 3)
    cellX += col.width
  })

  // Draw bottom border
  const totalWidth = columns.reduce((sum, col) => sum + col.width, 0)
  doc.setDrawColor(220, 220, 220)
  doc.line(x, y + rowHeight, x + totalWidth, y + rowHeight)

  return y + rowHeight
}

function checkPageBreak(
  doc: jsPDF,
  currentY: number,
  needed: number,
  margin: number,
): number {
  const pageHeight = doc.internal.pageSize.getHeight()
  if (currentY + needed > pageHeight - 40) {
    doc.addPage()
    return margin
  }
  return currentY
}

// Core function to generate the jsPDF document
async function createProgressReportDoc(
  options: ProgressReportOptions,
): Promise<jsPDF> {
  const {
    studentName,
    instrument,
    teacherName,
    dateRange,
    sections,
    lessons,
    tasks,
    metrics,
    branding,
    messageToParent,
  } = options

  const doc = new jsPDF('p', 'pt', 'a4')
  const pageWidth = doc.internal.pageSize.getWidth()
  const margin = 40
  const contentWidth = pageWidth - margin * 2
  let currentY = margin

  const formatDate = (date: Date | string) => {
    const d =
      typeof date === 'string' && date.length === 10
        ? new Date(date + 'T00:00:00')
        : new Date(date)
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const formatTime = (date: Date | string) => {
    const d = new Date(date)
    return d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  const headerColor = branding?.headerColor || [26, 42, 82] // Default Navy

  // --- HEADER ---
  currentY = 36

  // Optional Logo
  if (branding?.logoBase64) {
    try {
      // Assuming a standard aspect ratio, adjust width/height as needed
      doc.addImage(branding.logoBase64, 'PNG', margin, currentY - 10, 40, 40)
      doc.setFontSize(20)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(headerColor[0], headerColor[1], headerColor[2])
      doc.text('Student Progress Report', margin + 50, currentY + 15)
      currentY += 40
    } catch (e) {
      console.error('Failed to add logo to PDF', e)
      doc.setFontSize(20)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(headerColor[0], headerColor[1], headerColor[2])
      doc.text('Student Progress Report', margin, currentY)
      currentY += 28
    }
  } else {
    doc.setFontSize(20)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(headerColor[0], headerColor[1], headerColor[2])
    doc.text('Student Progress Report', margin, currentY)
    currentY += 28
  }

  doc.setFontSize(11)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(60, 60, 60)

  doc.text(`Student: ${studentName}`, margin, currentY)
  doc.text(`Instrument: ${instrument || 'Music'}`, margin, currentY + 16)

  const rightColX = pageWidth / 2 + 20
  doc.text(`Teacher: ${teacherName}`, rightColX, currentY)
  doc.text(
    `Period: ${formatDate(dateRange.from)} – ${formatDate(dateRange.to)}`,
    rightColX,
    currentY + 16,
  )

  currentY += 42
  doc.setDrawColor(200, 200, 200)
  doc.line(margin, currentY, pageWidth - margin, currentY)
  currentY += 24

  // --- MESSAGE FROM TEACHER ---
  if (messageToParent && messageToParent.trim()) {
    doc.setFontSize(14)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(headerColor[0], headerColor[1], headerColor[2])
    doc.text('Message from Teacher', margin, currentY)
    currentY += 20

    doc.setFontSize(10)
    doc.setFont('helvetica', 'italic')
    doc.setTextColor(60, 60, 60)

    // Word-wrap the message to fit within content width
    const messageLines = doc.splitTextToSize(
      messageToParent.trim(),
      contentWidth,
    )
    messageLines.forEach((line: string) => {
      currentY = checkPageBreak(doc, currentY, 16, margin)
      doc.text(line, margin, currentY)
      currentY += 14
    })

    currentY += 16
    doc.setDrawColor(230, 230, 230)
    doc.line(margin, currentY, pageWidth - margin, currentY)
    currentY += 20
  }

  // --- PROGRESS SUMMARY ---
  if (sections.progressSummary && metrics) {
    doc.setFontSize(14)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(headerColor[0], headerColor[1], headerColor[2])
    doc.text('Progress Summary', margin, currentY)
    currentY += 20

    // Use the canonical display-status logic so the PDF summary matches
    // the live Reports dashboard. A lesson with status COMPLETED but
    // attended === false is a No-Show, NOT a completed lesson.
    const completedLessons = lessons.filter(
      (l) => getLessonDisplayStatus(l) === 'completed',
    ).length
    const totalLessons = lessons.length
    const completedTasksCount = tasks.filter(
      (t) => t.status === 'completed',
    ).length
    const totalTasks = tasks.length

    const colWidth = contentWidth / 3
    const summaryColumns: TableColumn[] = [
      { header: 'Total Lessons', width: colWidth },
      { header: 'Completed Tasks', width: colWidth },
      { header: 'Attendance Rate', width: colWidth },
    ]

    currentY = drawTableHeader(
      doc,
      summaryColumns,
      margin,
      currentY,
      28,
      headerColor,
      [255, 255, 255],
    )
    currentY = drawTableRow(
      doc,
      summaryColumns,
      [
        `${completedLessons} / ${totalLessons}`,
        `${completedTasksCount} / ${totalTasks}`,
        `${metrics.attendanceRate}%`,
      ],
      margin,
      currentY,
      28,
      false,
    )

    currentY += 28
  }

  // --- LESSON HISTORY ---
  if (sections.lessonHistory) {
    currentY = checkPageBreak(doc, currentY, 80, margin)

    doc.setFontSize(14)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(headerColor[0], headerColor[1], headerColor[2])
    doc.text('Lesson History', margin, currentY)
    currentY += 20

    if (lessons.length === 0) {
      doc.setFontSize(10)
      doc.setFont('helvetica', 'italic')
      doc.setTextColor(130, 130, 130)
      doc.text('No lessons found in this period.', margin, currentY)
      currentY += 24
    } else {
      const sortedLessons = [...lessons].sort(
        (a, b) => new Date(b.startAt).getTime() - new Date(a.startAt).getTime(),
      )

      const lessonColumns: TableColumn[] = sections.lessonNotes
        ? [
            { header: 'Date', width: contentWidth * 0.18 },
            { header: 'Time', width: contentWidth * 0.15 },
            { header: 'Status', width: contentWidth * 0.15 },
            { header: 'Notes', width: contentWidth * 0.52 },
          ]
        : [
            { header: 'Date', width: contentWidth * 0.35 },
            { header: 'Time', width: contentWidth * 0.3 },
            { header: 'Status', width: contentWidth * 0.35 },
          ]

      currentY = drawTableHeader(
        doc,
        lessonColumns,
        margin,
        currentY,
        24,
        [240, 240, 240],
        [40, 40, 40],
      )

      sortedLessons.forEach((lesson, i) => {
        currentY = checkPageBreak(doc, currentY, 24, margin)

        // Use the canonical status helper so the PDF label exactly matches
        // what the student / teacher sees in the live dashboard. This correctly
        // distinguishes Completed vs No-Show (status COMPLETED + attended:false)
        // vs Cancelled vs Needs Confirmation vs Scheduled / Today / In Progress.
        const displayStatus = getLessonDisplayStatus(lesson)
        const statusText = PDF_STATUS_LABEL[displayStatus] || 'Scheduled'

        const row = [
          formatDate(lesson.startAt),
          formatTime(lesson.startAt),
          statusText,
        ]
        if (sections.lessonNotes) {
          row.push(lesson.notes || '—')
        }

        currentY = drawTableRow(
          doc,
          lessonColumns,
          row,
          margin,
          currentY,
          24,
          i % 2 === 1,
        )
      })

      currentY += 24
    }
  }

  // --- COMPLETED TASKS ---
  if (sections.completedTasks) {
    const completedTasksList = tasks.filter((t) => t.status === 'completed')

    currentY = checkPageBreak(doc, currentY, 80, margin)

    doc.setFontSize(14)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(headerColor[0], headerColor[1], headerColor[2])
    doc.text('Completed Tasks', margin, currentY)
    currentY += 20

    if (completedTasksList.length === 0) {
      doc.setFontSize(10)
      doc.setFont('helvetica', 'italic')
      doc.setTextColor(130, 130, 130)
      doc.text('No completed tasks found in this period.', margin, currentY)
      currentY += 24
    } else {
      const sortedTasks = [...completedTasksList].sort((a, b) => {
        const dateA = new Date(a.completedDate || a.dueDate || 0).getTime()
        const dateB = new Date(b.completedDate || b.dueDate || 0).getTime()
        return dateB - dateA
      })

      const taskColumns: TableColumn[] = [
        { header: 'Task', width: contentWidth * 0.45 },
        { header: 'Due Date', width: contentWidth * 0.275 },
        { header: 'Completed On', width: contentWidth * 0.275 },
      ]

      currentY = drawTableHeader(
        doc,
        taskColumns,
        margin,
        currentY,
        24,
        [240, 240, 240],
        [40, 40, 40],
      )

      sortedTasks.forEach((task, i) => {
        currentY = checkPageBreak(doc, currentY, 24, margin)

        currentY = drawTableRow(
          doc,
          taskColumns,
          [
            task.title,
            task.dueDate ? formatDate(task.dueDate) : '—',
            task.completedDate ? formatDate(task.completedDate) : '—',
          ],
          margin,
          currentY,
          24,
          i % 2 === 1,
        )
      })

      currentY += 24
    }
  }

  // --- FOOTER on every page ---
  const pageCount = doc.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i)
    doc.setFontSize(9)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(150, 150, 150)
    doc.text(
      `Generated by Maestro Connect · ${formatDate(new Date())}`,
      margin,
      doc.internal.pageSize.getHeight() - 20,
    )
    doc.text(
      `Page ${i} of ${pageCount}`,
      pageWidth - margin - 50,
      doc.internal.pageSize.getHeight() - 20,
    )
  }

  return doc
}

export async function generateProgressReportBlob(
  options: ProgressReportOptions,
): Promise<Blob> {
  const doc = await createProgressReportDoc(options)
  return doc.output('blob')
}

export async function generateProgressReportBase64(
  options: ProgressReportOptions,
): Promise<string> {
  const doc = await createProgressReportDoc(options)
  return doc.output('datauristring')
}

export async function generateProgressReport(
  options: ProgressReportOptions,
): Promise<void> {
  const doc = await createProgressReportDoc(options)
  const safeStudentName = options.studentName
    .replace(/[^a-z0-9]/gi, '_')
    .toLowerCase()
  const dateStr = new Date().toISOString().split('T')[0]
  doc.save(`${safeStudentName}_progress_report_${dateStr}.pdf`)
}
